import {useEffect, useRef, useState} from 'react';
import i18next from 'i18next';
import {Moment} from 'moment';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptLocale from '@fullcalendar/core/locales/pt';
import esLocale from '@fullcalendar/core/locales/es';
import {
  DateSelectArg,
  DateSpanApi,
  EventApi,
  EventChangeArg,
  EventContentArg,
  EventInput,
  EventMountArg,
} from '@fullcalendar/core';
import {useTheme} from '@mui/material';
import {AppLanguageTag} from '../../i18n/types';
import {DateRangeType} from '../date-range-input/date-range-utils';
import {useResizeObserver} from '../../hooks/use-resize-observer';
import './calendar.scss';

export type CalendarProps = {
  data: CalendarData;
  dataLoading: boolean;
  dataUpdating?: boolean;
  range: DateRangeType;
  date: Moment;
  allDaySlot?: boolean;
  editable?: boolean;
  selectable?: boolean;
  selectAllow?: (selectionInfo: DateSpanApi) => boolean;
  onClickItem: OnCalendarClickEvent;
  onCreateSelection?: OnCalendarCreateSelection;
  onEditSelection?: OnCalendarEditSelection;
  eventDidMount?: (info: EventMountArg) => void;
  eventContent?: (eventInfo: EventContentArg) => void;
  className?: string;
};

export type CalendarDataEvent = EventInput;

export type CalendarData = {
  /** https://fullcalendar.io/docs/event-object */
  events: EventInput[];
  /** e.g: "06:00" */
  minTime?: string;
};

export type OnCalendarClickEvent = (
  evt: React.MouseEvent<HTMLElement>,
  calendarEvt: EventApi
) => void;

export type OnCalendarCreateSelection = (selectionInfo: DateSelectArg) => void;

export type OnCalendarEditSelection = (changeInfo: EventChangeArg) => void;

const SLICE_SIZE = 100;

export function Calendar({
  data,
  dataLoading,
  dataUpdating,
  range,
  date,
  allDaySlot,
  eventDidMount,
  onCreateSelection,
  onEditSelection,
  onClickItem,
  eventContent,
  editable = !!onEditSelection,
  selectable = !!onCreateSelection,
  selectAllow,
  className,
}: CalendarProps) {
  const theme = useTheme();
  const fc = useRef<FullCalendar>(null);
  const calendarDivRef = useRef<HTMLDivElement>(null);
  const [events, setEvents] = useState<EventInput[]>([]);
  const [sliceIndex, setSliceIndex] = useState(0);
  const wasDataLoading = useRef(dataLoading);

  // Handle calendar size updates.
  useResizeObserver(
    calendarDivRef,
    () => {
      fc.current?.getApi().updateSize();
    },
    100
  );

  // Range or date changed. Reset.
  useEffect(() => {
    const fcApi = fc.current?.getApi();
    fcApi?.changeView(calendarRangeViewMap[range] ?? 'dayGridMonth', date.format('YYYY-MM-DD'));
    fcApi?.setOption('dayMaxEventRows', range === '1m' ? 4 : undefined);

    setEvents([]);
    setSliceIndex(0);
  }, [range, date]);

  // Re-draw events when items array have changed (e.g: filtering).
  useEffect(() => {
    if (!dataLoading && wasDataLoading.current === false) {
      // items were changed while not loading (filtered, added, removed, etc)
      if (data.events.length > SLICE_SIZE) {
        // If too many events, to add them in slices.
        // console.log('calendar: many data changed. re-slicing...');
        setEvents([]);
        setSliceIndex(0);
      } else {
        // console.log('calendar: data changed. setting all at once.');
        // else just set all events at once.
        setEvents(data.events);
      }
    }
  }, [data.events, dataLoading]);

  // Handle events as they come/change.
  useEffect(() => {
    // Add events to calendar.
    const concatEvents = () => {
      // console.log('calendar: processing events..', {sliceIndex, dataLoading});
      // ----------------
      // Two modes:
      // A) means no slicing, set events as they come.
      // B) means we are slicing the events data(load in chunks) to avoid blocking the UI when loading a lot of events.
      // ----------------

      // A) If items are loading, set events as they come
      if (dataLoading) {
        wasDataLoading.current = true;
        setEvents(data.events);
      } else {
        // A*) If items were loading, set remaining events once.
        if (wasDataLoading.current === true) {
          wasDataLoading.current = false;
          setEvents(data.events);
          setSliceIndex(Math.ceil(data.events.length / SLICE_SIZE));
          // console.log('calendar: data finished loading.');
          return;
        }

        // B) Add events in slices
        if (sliceIndex * SLICE_SIZE >= data.events.length) {
          // no more slicing needed.
          // console.log('calendar: all events sliced.');
          return;
        }
        // slice
        // console.log('calendar: slicing events..');
        const chunk = data.events.slice(sliceIndex * SLICE_SIZE, (sliceIndex + 1) * SLICE_SIZE);
        setEvents((prevEvents) => prevEvents.concat(chunk));
        setSliceIndex((x) => x + 1);
      }
    };

    const id = setTimeout(concatEvents, 10);

    return () => clearTimeout(id);
  }, [data.events, sliceIndex, dataLoading]);

  return (
    <div
      ref={calendarDivRef}
      className={className ? `calendar ${className}` : 'calendar'}
      // className={
      //   className
      //     ? `calendar ${className}` + (dataUpdating ? ' updating' : '')
      //     : 'calendar' + (dataUpdating ? ' updating' : '')
      // }
      style={{cursor: dataUpdating ? 'wait' : undefined}}
    >
      <div style={{pointerEvents: dataUpdating ? 'none' : undefined}}>
        <FullCalendar
          ref={fc}
          plugins={
            selectable
              ? [dayGridPlugin, timeGridPlugin, interactionPlugin]
              : [dayGridPlugin, timeGridPlugin]
          }
          locales={[ptLocale, esLocale]}
          firstDay={0} // force Sunday as first day
          headerToolbar={false}
          // dayMaxEventRows={3}
          height="auto"
          dayHeaderFormat={{
            weekday: 'short',
            day: range === '1d' || range === '1w' ? '2-digit' : undefined,
          }}
          eventTimeFormat={{hour: '2-digit', minute: '2-digit', hour12: false}}
          slotLabelFormat={{hour: '2-digit', minute: '2-digit', hour12: false}}
          initialView={calendarRangeViewMap[range]}
          slotMinTime={data.minTime}
          allDayText=""
          allDaySlot={allDaySlot}
          selectable={selectable}
          editable={editable}
          events={events}
          eventTextColor={theme.palette.gray.main}
          eventBorderColor={theme.palette.primary.main}
          eventBackgroundColor="#DAEEFC"
          eventClick={(info) => {
            onClickItem(info.jsEvent as unknown as React.MouseEvent<HTMLElement>, info.event);
          }}
          locale={appLangToFullCalendarLang[i18next.language as AppLanguageTag]}
          eventDidMount={eventDidMount}
          select={onCreateSelection}
          eventChange={onEditSelection}
          selectAllow={selectAllow}
          eventAllow={selectAllow}
          eventContent={eventContent}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// ---------------------------------------------------------------
// ---------------------------------------------------------------

const calendarRangeViewMap: Record<DateRangeType, string> = {
  '1d': 'timeGridDay',
  '1w': 'timeGridWeek',
  '1m': 'dayGridMonth',
  '7d': 'timeGridWeek',
  '15d': 'dayGridMonth',
  '30d': 'dayGridMonth',
  '180d': 'dayGridMonth',
  '365d': 'dayGridMonth',
  custom: 'dayGridMonth',
};

/** https://fullcalendar.io/docs/locale */
const appLangToFullCalendarLang: Record<AppLanguageTag, string> = {
  [AppLanguageTag.ptPT]: 'pt-pt',
  [AppLanguageTag.ptBR]: 'pt-br',
  [AppLanguageTag.esES]: 'es-es',
  [AppLanguageTag.glES]: 'es-es',
  [AppLanguageTag.enUS]: 'en-us',
};
